import React, { useRef, useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  Divider,
  FormControlLabel,
  Checkbox,
  Alert,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import { DialogMode } from '../type/DialogMode';
import { Enterprise, MerchantV2, User } from '../type';
import { CloseButton } from './CloseButton';
import { CancelButton } from './CancelButton';
import { sortedUsers } from '../utils/util';
import { renderEnterpriseTree } from '../hook/useEnterpriseTree';

interface MerchantV2DialogProps {
  open: boolean;
  mode: DialogMode;
  merchant?: MerchantV2;
  onClose: () => void;
  onSave: (merchant: MerchantV2) => void;
  onDelete?: (merchant_id: string) => void;
  i_users: User[];
  e_users: User[];
  ent: Enterprise[];
}

const MerchantV2Dialog: React.FC<MerchantV2DialogProps> = React.memo(({
  open,
  mode,
  merchant,
  onClose,
  onSave,
  onDelete,
  i_users,
  e_users,
  ent,
}) => {
  const [category_code, setCategoryCode] = useState('');
  const [merchant_id, setMerchantId] = useState('');
  const [merchant_image_url, setMerchantImageUrl] = useState('');
  const [merchant_name, setMerchantName] = useState('');
  const [merchant_url, setMerchantUrl] = useState('');
  const [isRecommended, setIsRecommended] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [independentUsers, setIndependentUsers] = useState<User[]>([]);
  const [selectedIndependentUsers, setSelectedIndependentUsers] = useState<User[]>([]);
  const [enterpriseUsers, setEnterpriseUsers] = useState<User[]>([]);
  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
  const [selectedEnterpriseUsers, setSelectedEnterpriseUsers] = useState<User[]>([]);

  // Set initial values when Dialog opens
  React.useEffect(() => {
    if (open) {
      if (mode === DialogMode.EDIT && merchant) {
        setCategoryCode(merchant.category_code);
        setMerchantId(merchant.merchant_id);
        setMerchantImageUrl(merchant.merchant_image_url);
        setMerchantName(merchant.merchant_name);
        setMerchantUrl(merchant.merchant_url);
        setIsRecommended(merchant.recommended !== false);
        setEnterprises(ent);
        const tempSelectedUsers = merchant.allowedUsers ? i_users.filter(u => merchant.allowedUsers?.includes(u.uid) && !u.isEnterpriseUser) : [];
        const tempUsers = merchant.allowedUsers ?
          i_users.filter(u => !merchant.allowedUsers?.includes(u.uid)) :
          tempSelectedUsers?.length > 0 ? i_users.filter(u => tempSelectedUsers.some(selected => selected.uid !== u.uid)) : i_users;
        setSelectedIndependentUsers(sortedUsers(tempSelectedUsers));
        setIndependentUsers(sortedUsers(tempUsers));

        const tempSelectedEnterpriseUsers = [...(merchant.allowedEnterprises ? e_users.filter(u => u.enterpriseId && merchant.allowedEnterprises?.includes(u.enterpriseId)) : []), ...(e_users.filter(u => merchant.allowedUsers?.includes(u.uid) && u.isEnterpriseUser))];
        const tempEnterpriseUsers = merchant.allowedEnterprises ?
          e_users.filter(u => u.enterpriseId && !merchant.allowedEnterprises?.includes(u.enterpriseId) && !merchant.allowedUsers?.includes(u.uid)) :
          tempSelectedEnterpriseUsers?.length > 0 ? e_users.filter(u => tempSelectedEnterpriseUsers.some(selected => selected.uid !== u.uid)) : e_users;
        setSelectedEnterpriseUsers(sortedUsers(tempSelectedEnterpriseUsers));
        setEnterpriseUsers(sortedUsers(tempEnterpriseUsers));
      } else {
        setCategoryCode('');
        setMerchantId('');
        setMerchantImageUrl('');
        setMerchantName('');
        setMerchantUrl('');
        setIsRecommended(true);
        setSelectedIndependentUsers([]);
        setSelectedEnterpriseUsers([]);
        if (mode === DialogMode.CREATE) {
          setEnterprises(ent);
          const tempSelectedUsers = i_users.filter(u => !u.isEnterpriseUser);
          const tempUsers = tempSelectedUsers?.length > 0 ? i_users.filter(u => tempSelectedUsers.some(selected => selected.uid !== u.uid)) : i_users;
          setIndependentUsers(sortedUsers(tempUsers));

          const tempSelectedEnterpriseUsers = [...(e_users.filter(u => u.enterpriseId)), ...(e_users.filter(u => u.isEnterpriseUser))];
          const tempEnterpriseUsers = tempSelectedEnterpriseUsers?.length > 0 ? e_users.filter(u => tempSelectedEnterpriseUsers.some(selected => selected.uid !== u.uid)) : e_users;
          setEnterpriseUsers(sortedUsers(tempEnterpriseUsers));
        } else {
          setEnterprises([]);
          setIndependentUsers([]);
          setEnterpriseUsers([]);
        }
      }
    }
  }, [open, mode, merchant]);

  const handleSave = useCallback(async () => {
    setUploadError(null);

    if (!category_code.trim()) {
      alert('카테고리 코드를 입력해주세요.');
      return;
    }
    if (!merchant_id.trim()) {
      alert('상점 ID를 입력해주세요.');
      return;
    }
    if (!merchant_image_url.trim()) {
      alert('상점 이미지 URL을 입력해주세요.');
      return;
    }
    if (!merchant_name.trim()) {
      alert('상점 이름을 입력해주세요.');
      return;
    }
    if (!merchant_url.trim()) {
      alert('상점 URL을 입력해주세요.');
      return;
    }

    const allowedEnterprises: string[] = [];
    const tmpEnterpriseUsers = isRecommended ? [] : [...selectedEnterpriseUsers];
    for (let i = tmpEnterpriseUsers.length - 1; i >= 0; i--) {
      const user = tmpEnterpriseUsers[i];
      if (user.enterpriseId) {
        const fullySelected = isEnterpriseFullySelected(user.enterpriseId!);
        if (fullySelected) {
          if (!allowedEnterprises.includes(user.enterpriseId)) {
            allowedEnterprises.push(user.enterpriseId);
          }
          tmpEnterpriseUsers.splice(i, 1);
        }
      } else {
        tmpEnterpriseUsers.splice(i, 1);
      }
    }
    const allowedUsers = isRecommended ? [] : [...selectedIndependentUsers, ...tmpEnterpriseUsers].map(u => u.uid);

    const merchantDoc: MerchantV2 = {
      category_code,
      merchant_id,
      merchant_image_url,
      merchant_name,
      merchant_url,
      recommended: isRecommended,
      allowedUsers,
      allowedEnterprises,
    };

    onSave(merchantDoc);
    handleClose();
  }, [category_code, merchant_id, merchant_image_url, merchant_name, merchant_url, isRecommended, onSave, selectedIndependentUsers, selectedEnterpriseUsers]);

  function isEnterpriseFullySelected(enterpriseId: string) {
    const remaining = enterpriseUsers.filter(user => user.enterpriseId === enterpriseId);
    return remaining.length === 0;
  }

  const handleDelete = useCallback(() => {
    if (mode === DialogMode.EDIT && merchant?.merchant_id && onDelete) {
      onDelete(merchant.merchant_id);
      handleClose();
    }
  }, [mode, merchant, onDelete]);

  const handleClose = useCallback(() => {
    setCategoryCode('');
    setMerchantId('');
    setMerchantImageUrl('');
    setMerchantName('');
    setMerchantUrl('');
    setIsRecommended(true);
    setUploadError(null);
    onClose();
  }, [onClose]);

  const handleCategoryCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCategoryCode(e.target.value);
  }, []);
  const handleMerchantIdChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setMerchantId(e.target.value);
  }, []);
  const handleMerchantImageUrlChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setMerchantImageUrl(e.target.value);
  }, []);
  const handleMerchantNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setMerchantName(e.target.value);
  }, []);
  const handleMerchantUrlChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setMerchantUrl(e.target.value);
  }, []);

  const handleRecommendedChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setIsRecommended(e.target.checked);
  }, []);

  const getDialogTitle = () => {
    return mode === DialogMode.CREATE ? '새 상점 작성' : '상점 수정';
  };

  const getSaveButtonText = () => {
    return mode === DialogMode.CREATE ? '저장' : '수정';
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >

      {/* Top close button and title */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 3, pb: 2 }}>
        <Typography variant="h6" sx={{ fontSize: '1.25rem' }}>
          {getDialogTitle()}
        </Typography>
        <CloseButton onClose={onClose} />
      </Box>
      <Divider />
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 3 }}>
          {/* 왼쪽 컬럼: 제목, 배너 이미지, 업로드 타입 선택 */}
          <Box
            sx={{
              flex: 0.9,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              mt: 0.5,
              border: '1px solid #ccc',
              borderRadius: 2,
              padding: 2,
              height: 500,
              overflow: 'auto',
            }}>
            {uploadError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {uploadError}
              </Alert>
            )}
            <TextField
              label="Category Code"
              value={category_code}
              onChange={handleCategoryCodeChange}
              fullWidth
            />

            <TextField
              label="Merchant ID"
              value={merchant_id}
              onChange={handleMerchantIdChange}
              fullWidth
            />
            <TextField
              label="Merchant Image Url"
              value={merchant_image_url}
              onChange={handleMerchantImageUrlChange}
              fullWidth
            />
            <TextField
              label="Merchant Name"
              value={merchant_name}
              onChange={handleMerchantNameChange}
              fullWidth
            />
            <TextField
              label="Merchant URL"
              value={merchant_url}
              onChange={handleMerchantUrlChange}
              fullWidth
            />
          </Box>

          {/* 오른쪽 컬럼: 공개 여부 및 사용자 선택 */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isRecommended}
                  onChange={handleRecommendedChange}
                />
              }
              label="전체 추천"
            />

            {isRecommended ? (
              <Typography variant="body2" color="textSecondary">
                모든 사용자가 이 상점을 볼 수 있습니다.
              </Typography>
            ) : (
              <>
                <Typography variant="body2" color="textSecondary">
                  상점을 볼 수 있는 사용자를 선택하세요.
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    mt: 3,
                    border: '1px solid #ccc',
                    borderRadius: 2,
                    padding: 2,
                  }}>
                  {/* 미선택 사용자 */}
                  <Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>미선택 개인 사용자</Typography>

                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                          setSelectedIndependentUsers(prev => sortedUsers([...prev, ...independentUsers]));
                          setIndependentUsers([]);
                        }}
                      >
                        전체 선택
                      </Button>
                    </Box>
                    <Table size="small">
                      <TableBody>
                        {independentUsers.map(user => (
                          <TableRow key={user.uid}>
                            <TableCell>{user.uid}</TableCell>
                            <TableCell>{user.displayName}</TableCell>
                            <TableCell>{user.phoneNumber}</TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                sx={{ minWidth: '60px', padding: '4px 8px' }}
                                onClick={() => {
                                  setIndependentUsers(sortedUsers(independentUsers.filter(u => u.uid !== user.uid)));
                                  setSelectedIndependentUsers(sortedUsers([...selectedIndependentUsers, user]));
                                }}>선택</Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>

                  <Divider sx={{ borderBottomWidth: '2px', borderColor: '#444' }} />

                  {/* 선택된 사용자 */}
                  <Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>선택된 개인 사용자</Typography>

                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setIndependentUsers(prev => sortedUsers([...prev, ...selectedIndependentUsers]));
                          setSelectedIndependentUsers([]);
                        }}
                      >
                        전체 미선택
                      </Button>
                    </Box>
                    <Table size="small">
                      <TableBody>
                        {selectedIndependentUsers.map(user => (
                          <TableRow key={user.uid}>
                            <TableCell>{user.uid}</TableCell>
                            <TableCell>{user.displayName}</TableCell>
                            <TableCell>{user.phoneNumber}</TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                sx={{ minWidth: '60px', padding: '4px 8px' }}
                                onClick={() => {
                                  setSelectedIndependentUsers(sortedUsers(selectedIndependentUsers.filter(u => u.uid !== user.uid)));
                                  setIndependentUsers(sortedUsers([...independentUsers, user]));
                                }}>미선택</Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    mt: 3,
                    border: '1px solid #ccc',
                    borderRadius: 2,
                    padding: 2,
                  }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>미선택 기업 사용자</Typography>
                    {renderEnterpriseTree(enterpriseUsers, enterprises, false, selectedEnterpriseUsers, setSelectedEnterpriseUsers, enterpriseUsers, setEnterpriseUsers)}
                  </Box>

                  <Divider sx={{ borderBottomWidth: '2px', borderColor: '#444' }} />

                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>선택된 기업 사용자</Typography>
                    {renderEnterpriseTree(selectedEnterpriseUsers, enterprises, true, selectedEnterpriseUsers, setSelectedEnterpriseUsers, enterpriseUsers, setEnterpriseUsers)}
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 1, justifyContent: 'space-between' }}>
        <Box>
          {mode === DialogMode.EDIT && merchant?.merchant_id && onDelete && (
            <Button
              onClick={handleDelete}
              sx={{
                backgroundColor: '#EC2D4D',
                '&:hover': {
                  backgroundColor: '#F26C82'
                }
              }}
            >
              삭제
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <CancelButton onClick={handleClose}>닫기</CancelButton>
          <Button
            onClick={handleSave}
            disabled={isUploading}
          >
            {isUploading ? '업로드 중...' : getSaveButtonText()}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
});

export default MerchantV2Dialog;
